// ════════════════════════════════════════════════════════════════════
// LYRAEA — the listening room
// ────────────────────────────────────────────────────────────────────
// Five shelves and a studio:
//   LIBRARY — everything published, newest first, plus what's on repeat
//   LIKED   — what this listener kept
//   SETS    — their own playlists
//   SHELF   — the things that are not music. Art, writing, books.
//   STUDIO  — publish and manage your own releases (owner-side)
//
// Plus one page that is not a shelf: THE ARTIST, reached by tapping any
// name. An artist in Lyraea is a Mark, not a string — see ArtistPage.js.
//
// The whole app is a list and a bar. That is the brief: Soundcloud's
// openness with Spotify's ease, and "ease" means a listener never has to
// learn anything. Tap a row, it plays. Tap the bar, it opens.
//
// ⟡ THE SHELF OPENS AT ZERO. A free thing can be taken and kept, today.
// A priced thing still cannot be bought, and the code to charge for one
// does not exist. See the VENDING block in lyraeaData.js for why the
// free half was always safe and the priced half still is not.
// ════════════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from "react";
import {
  useCatalogue, useMyLane, useSets, toggleLike, countPlay,
  publishTrack, unpublishTrack, createSet, deleteSet, addToSet,
  removeFromSet, resolveSet, probeDuration, setTrackDuration,
  useAlbums, saveAlbum, deleteAlbum, setTrackAlbum, albumTracks,
  fmtTime, fmtPlays,
  useExtras, extrasFor, saveExtra, deleteExtra, orphanExtras, sweepOrphanExtras,
  useItems, publishItem, unpublishItem,
  useOwned, acquire, release,
  EXTRA_KINDS, ITEM_KINDS, KIND_META, holdsBody, PRICED_NOTICE,
} from "./lyraeaData";
import {
  useAudio, playTrack, loadQueue, setPlayReporter, restoreSession,
  formatProblem, attachKeyboard, toggleShuffle, addToQueue, getAudioState,
} from "./audio";
import { NowBar, NowSheet, Cover, Equalizer } from "./Player";
import { Viewer, ExtrasPanel } from "./Extras";
import { resolveTrack } from "./CDN";
import ArtistPage from "./ArtistPage";
import AlbumPage, { AlbumRail } from "./AlbumPage";
import { openBook } from "./book";

const C = {
  void: "var(--void)", obsidian: "var(--obsidian)", slab: "var(--slab)",
  slabHi: "var(--slab-hi)", edge: "var(--edge)", edgeHot: "var(--edge-hot)",
  gold: "var(--gold)", goldHi: "var(--gold-hi)", violet: "var(--violet)",
  bone: "var(--bone)", ash: "var(--ash)", smoke: "var(--smoke)", verd: "var(--verd)",
  display: "var(--display)", mono: "var(--mono)", sans: "var(--sans)",
};

const PIER_URL = "https://unstable-pierre.pages.dev";

const Kicker = ({ children, tint = C.smoke, size = 9 }) => (
  <div style={{
    fontFamily: C.mono, fontSize: size, letterSpacing: ".28em",
    textTransform: "uppercase", color: tint,
  }}>{children}</div>
);

const field = {
  width: "100%", boxSizing: "border-box", background: C.void,
  border: `1px solid ${C.edge}`, color: C.bone,
  fontFamily: C.sans, fontSize: 15, padding: "9px 11px", outline: "none",
};

// One column, centred, capped. Full-bleed rows on a 27" monitor read as a
// spreadsheet, not a record shelf — but the chrome (the masthead rule, the
// player bar) still spans edge to edge, which is what keeps it looking like
// the site rather than a boxed-in web page.
const WRAP = { width: "100%", maxWidth: 940, margin: "0 auto" };

// The site numbers its sections. It costs one line and it is most of why
// a page reads as "composed" rather than "a list of divs".
const Section = ({ idx, title, tint = C.smoke, right }) => (
  <div style={{ margin: "0 0 10px" }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
      <span style={{ fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".2em", color: C.edgeHot }}>
        {String(idx).padStart(2, "0")}
      </span>
      <span style={{
        fontFamily: C.mono, fontSize: 9, letterSpacing: ".28em",
        textTransform: "uppercase", color: tint,
      }}>{title}</span>
      <span style={{
        flex: 1, height: 1, alignSelf: "center",
        background: `linear-gradient(90deg, ${C.edge}, transparent)`,
      }} />
      {right}
    </div>
  </div>
);

const ghostBtn = {
  background: "none", border: `1px solid ${C.edge}`, color: C.smoke,
  fontFamily: C.mono, fontSize: 9, letterSpacing: ".2em", padding: "6px 9px",
};

// Swap a track's running-order number with its neighbour's. Two writes,
// no renumbering — see the note at the call site for why that matters.
// Tracks that have never been ordered all sit at 0, so the first press
// on such an album needs somewhere to go: the neighbour is found by the
// SORTED position rather than by the number, and a tie is broken by
// giving the moved track a number of its own.
const swapOrder = async (uid, track, allTracks, dir, onFlash) => {
  const sibs = albumTracks(track.albumId, allTracks);
  const i = sibs.findIndex((x) => x.id === track.id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= sibs.length) return onFlash(dir < 0 ? "ALREADY FIRST" : "ALREADY LAST");
  const other = sibs[j];
  const a = Number(track.order) || 0;
  const b = Number(other.order) || 0;
  // Everything at 0: give them real positions instead of swapping two
  // zeroes and appearing to do nothing.
  const [mineTo, theirsTo] = a === b ? (dir < 0 ? [j, i] : [j, i]) : [b, a];
  try {
    await setTrackAlbum(uid, track.id, track.albumId, mineTo);
    await setTrackAlbum(uid, other.id, other.albumId, theirsTo);
    onFlash("MOVED");
  } catch (e) { onFlash((e.message || "REFUSED").toUpperCase()); }
};

// ════════════════════════════════════════════════════════════════════
// A ROW — the atom of the whole app
// ════════════════════════════════════════════════════════════════════
function TrackRow({
  track, index, playing, current, liked, locked, liveDuration,
  onPlay, onLike, onArtist, onAdd, onRemove, onQueue, hasExtras,
}) {
  // A track published before the metadata probe existed stores 0. While it
  // is the one playing, the engine knows the truth — show that rather than
  // a confident, wrong 0:00.
  const length = track.durationSec || (current ? liveDuration : 0);
  return (
    <div
      className={`ly-row${current ? " on" : ""}`}
      onClick={() => !locked && onPlay()}
      style={{
        display: "flex", alignItems: "center", gap: 11,
        padding: "9px 6px",
        cursor: locked ? "default" : "pointer",
        opacity: locked ? 0.55 : 1,
      }}
    >
      <div style={{ width: 20, display: "grid", placeItems: "center" }}>
        {current
          ? <Equalizer color={playing ? C.verd : C.smoke} size={11} />
          : <span style={{ fontFamily: C.mono, fontSize: 10, color: C.smoke }}>
              {String(index + 1).padStart(2, "0")}
            </span>}
      </div>

      <div style={{ position: "relative", lineHeight: 0, flex: "0 0 auto" }}>
        <Cover src={track.cover} seed={track.id} title={track.title} size={42} />
        {!locked && (
          <div className="ly-play" style={{
            position: "absolute", inset: 0, display: "grid", placeItems: "center",
            background: "rgba(6,6,10,.55)", color: C.goldHi,
            fontFamily: C.display, fontSize: 15,
          }}>{current && playing ? "❚❚" : "▶"}</div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ly-title" style={{
          fontFamily: C.sans, fontSize: 16, letterSpacing: ".02em",
          color: current ? C.goldHi : C.bone,
          transition: "color .25s",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{track.title}</div>
        <div
          onClick={(e) => { if (onArtist && track.artistUid) { e.stopPropagation(); onArtist(track.artistUid); } }}
          style={{
            fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".12em", color: C.smoke,
            textTransform: "uppercase", overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap",
            display: "inline-block", maxWidth: "100%",
            cursor: onArtist && track.artistUid ? "pointer" : "default",
          }}
        >
          {track.artist || "UNKNOWN"}
          {track.plays ? ` · ${fmtPlays(track.plays)} PLAYS` : ""}
          {/* A silent tell that there is something behind the track, so
              nobody has to open the sheet on every row to find out. */}
          {hasExtras ? " · ♪+" : ""}
        </div>
      </div>

      <span style={{
        fontFamily: C.mono, fontSize: 10,
        color: locked ? C.gold : C.smoke, whiteSpace: "nowrap",
      }}>
        {locked ? `◆ ${track.priceDC}` : fmtTime(length)}
      </span>

      {/* Queue it instead of playing it. Deliberately NOT offered on a
          locked row — a queue full of things that refuse to play is a
          worse experience than not being able to queue them. */}
      {onQueue && !locked && (
        <button onClick={(e) => { e.stopPropagation(); onQueue(); }} title="Add to the queue"
          style={{ background: "none", border: "none", padding: "4px 2px", color: C.edgeHot, fontSize: 13, fontFamily: C.display }}>
          ≡
        </button>
      )}

      {onAdd && (
        <button onClick={(e) => { e.stopPropagation(); onAdd(); }} title="Add to a set"
          style={{ background: "none", border: "none", padding: "4px 2px", color: C.edgeHot, fontSize: 15, fontFamily: C.display }}>
          ＋
        </button>
      )}

      {onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Remove from this set"
          style={{ background: "none", border: "none", padding: "4px 2px", color: C.edgeHot, fontSize: 13, fontFamily: C.mono }}>
          ✕
        </button>
      )}

      <button
        onClick={(e) => { e.stopPropagation(); onLike(); }}
        title={liked ? "Unlike" : "Like"}
        style={{
          background: "none", border: "none", padding: "4px 2px",
          color: liked ? C.gold : C.edgeHot, fontSize: 15, fontFamily: C.display,
        }}
      >{liked ? "◆" : "◇"}</button>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ADD-TO-SET — a sheet, because a dropdown on a phone is a dark art
// ════════════════════════════════════════════════════════════════════
function AddToSet({ uid, track, sets, onClose, onFlash }) {
  const [name, setName] = useState("");

  const add = async (setId) => {
    try { await addToSet(uid, setId, track.id); onFlash("ADDED"); onClose(); }
    catch (e) { onFlash(e.message); }
  };

  const make = async () => {
    if (!name.trim()) return onFlash("NEEDS A NAME");
    try {
      const id = await createSet(uid, name.trim());
      await addToSet(uid, id, track.id);
      onFlash("SET MADE");
      onClose();
    } catch (e) { onFlash(e.message); }
  };

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{
      position: "fixed", inset: 0, zIndex: 950, background: "rgba(3,3,6,.82)",
      backdropFilter: "blur(6px)", display: "flex", alignItems: "flex-end",
      animation: "lyFade .18s both",
    }}>
      <div style={{
        width: "100%", background: `linear-gradient(180deg, ${C.slab}, ${C.void})`,
        borderTop: `1px solid ${C.edge}`, padding: `16px 18px calc(20px + var(--safe-b))`,
        maxHeight: "72vh", overflowY: "auto",
        animation: "lyRise .25s cubic-bezier(.22,.61,.36,1) both",
      }} className="ly-scroll">
        <Kicker tint={C.gold}>ADD TO A SET</Kicker>
        <div style={{
          fontFamily: C.display, fontSize: 20, color: C.bone, margin: "6px 0 14px",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{track.title}</div>

        {sets.list.map((s) => (
          <div key={s.id} onClick={() => add(s.id)} style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "11px 8px", borderBottom: `1px solid ${C.edge}`, cursor: "pointer",
          }}>
            <span style={{ fontFamily: C.sans, fontSize: 15.5, color: C.bone }}>{s.name}</span>
            <span style={{ fontFamily: C.mono, fontSize: 9, color: C.smoke, letterSpacing: ".16em" }}>
              {s.tracks[track.id] ? "ALREADY IN" : `${s.count} TRACKS`}
            </span>
          </div>
        ))}

        <div style={{ height: 16 }} />
        <Kicker>OR MAKE A NEW ONE</Kicker>
        <div style={{ display: "flex", gap: 7, marginTop: 8 }}>
          <input style={{ ...field, flex: 1 }} placeholder="Set name" value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && make()} />
          <button onClick={make} style={{
            background: `linear-gradient(150deg, ${C.goldHi}, ${C.gold})`, border: "none",
            color: C.void, fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".2em", padding: "0 16px",
          }}>MAKE</button>
        </div>

        <button onClick={onClose} style={{ ...ghostBtn, width: "100%", marginTop: 16, padding: "11px 0" }}>
          CLOSE
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STUDIO — publishing. Owner-side, and the only writer of tracks.
// ════════════════════════════════════════════════════════════════════
function Studio({ uid, artistName, mine, albums, allTracks, extras, items, onFlash }) {
  const blank = { title: "", artist: artistName || "", src: "", cover: "", durationSec: 0, priceDC: 0 };
  const [d, setD] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [probing, setProbing] = useState(false);

  // Ask the FILE how long it is instead of asking the human. Fires when
  // the filename field loses focus; failure is silent and non-blocking,
  // because a missing duration is cosmetic and a blocked publish is not.
  const probe = async () => {
    const src = d.src.trim();
    if (!src || formatProblem(src)) return;
    setProbing(true);
    const secs = await probeDuration(resolveTrack(src));
    setProbing(false);
    if (secs) { setD((cur) => ({ ...cur, durationSec: secs })); onFlash(`READ ${fmtTime(secs)}`); }
  };

  const submit = async () => {
    if (!d.title.trim()) return onFlash("NEEDS A TITLE");
    if (!d.src.trim())   return onFlash("NEEDS A FILE");
    const problem = formatProblem(d.src);
    if (problem) return onFlash(problem.toUpperCase());
    setBusy(true);
    try {
      await publishTrack(uid, {
        ...d,
        durationSec: Number(d.durationSec) || 0,
        priceDC: Number(d.priceDC) || 0,
      });
      setD({ ...blank, artist: d.artist });
      onFlash("PUBLISHED");
    } catch (e) { onFlash(e.message || "REFUSED"); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ animation: "lyFade .25s both" }}>
      <Kicker tint={C.gold}>YOUR CATALOGUE — {mine.length}</Kicker>
      <div style={{ height: 10 }} />
      {mine.length === 0 && (
        <div style={{ fontFamily: C.sans, fontSize: 14, color: C.smoke, paddingBottom: 8 }}>
          Nothing published yet.
        </div>
      )}
      {mine.map((t) => (
        <div key={t.id} style={{
          display: "flex", alignItems: "center", gap: 10, padding: "8px 6px",
          borderBottom: `1px solid ${C.edge}`,
        }}>
          <Cover src={t.cover} seed={t.id} title={t.title} size={34} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: C.sans, fontSize: 15, color: C.bone, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t.title}
            </div>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: C.smoke, letterSpacing: ".12em" }}>
              {t.priceDC ? `◆ ${t.priceDC}` : "FREE"} · {fmtPlays(t.plays)} PLAYS · {fmtTime(t.durationSec)}
            </div>

            {/* Album membership lives on the TRACK, so this is where it is
                edited — one select, one running-order number, and the album
                page re-sorts itself the instant either changes. */}
            <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center" }}>
              <select
                value={t.albumId || ""}
                onChange={(e) => setTrackAlbum(uid, t.id, e.target.value, t.order)
                  .then(() => onFlash(e.target.value ? "PUT ON ALBUM" : "MADE A SINGLE"))
                  .catch((err) => onFlash(err.message))}
                style={{
                  background: C.void, border: `1px solid ${C.edge}`, color: C.ash,
                  fontFamily: C.mono, fontSize: 9, letterSpacing: ".1em", padding: "4px 6px",
                  maxWidth: 190,
                }}>
                <option value="">— single —</option>
                {albums.list.filter((al) => al.artistUid === uid).map((al) => (
                  <option key={al.id} value={al.id}>{al.title}</option>
                ))}
              </select>
              {/* ── RUNNING ORDER ─────────────────────────────────────
                  Two arrows and a number, not drag-and-drop. Membership
                  lives on the TRACK (`order`), so a move is a write to
                  ONE row — which is the whole reason it can be arrows at
                  all. Drag would need a list to reorder, and the album
                  deliberately does not hold one.

                  ⚠ A SWAP, NOT A SHUFFLE. Moving a track up trades
                  `order` with its neighbour rather than renumbering the
                  record. Renumbering means N writes where one of them
                  can fail, leaving an album that disagrees with itself;
                  a swap is two writes and the worst case is two tracks
                  sharing a number, which sorts by title and looks fine. */}
              {t.albumId && (
                <>
                  <button
                    title="Earlier in the running order"
                    onClick={() => swapOrder(uid, t, allTracks, -1, onFlash)}
                    style={{ ...ghostBtn, padding: "4px 7px", letterSpacing: 0 }}>▲</button>
                  <button
                    title="Later in the running order"
                    onClick={() => swapOrder(uid, t, allTracks, +1, onFlash)}
                    style={{ ...ghostBtn, padding: "4px 7px", letterSpacing: 0 }}>▼</button>
                  <input
                    type="number" min={0} defaultValue={t.order || 0} title="Running order"
                    key={`ord-${t.id}-${t.order}`}
                    onBlur={(e) => setTrackAlbum(uid, t.id, t.albumId, e.target.value)
                      .then(() => onFlash("ORDER SET")).catch((err) => onFlash(err.message))}
                    style={{
                      width: 48, background: C.void, border: `1px solid ${C.edge}`,
                      color: C.ash, fontFamily: C.mono, fontSize: 9, padding: "4px 6px",
                    }} />
                </>
              )}
            </div>
          </div>
          <button
            onClick={() => unpublishTrack(uid, t.id).then(() => onFlash("REMOVED")).catch((e) => onFlash(e.message))}
            style={ghostBtn}>REMOVE</button>
        </div>
      ))}

      <AlbumStudio uid={uid} artistName={artistName} albums={albums}
        allTracks={allTracks} onFlash={onFlash} />

      <div style={{ height: 22 }} />
      <Kicker tint={C.gold}>PUBLISH</Kicker>
      <div style={{ height: 10 }} />
      <input style={field} placeholder="Title" value={d.title}
        onChange={(e) => setD({ ...d, title: e.target.value })} />
      <div style={{ height: 7 }} />
      <input style={field} placeholder="Artist" value={d.artist}
        onChange={(e) => setD({ ...d, artist: e.target.value })} />
      <div style={{ height: 7 }} />
      <input style={field} placeholder="Audio filename on lyraea-assets  (newbie.mp3)" value={d.src}
        onChange={(e) => setD({ ...d, src: e.target.value })}
        onBlur={probe} />
      <div style={{ height: 7 }} />
      <input style={field} placeholder="Cover filename or full URL" value={d.cover}
        onChange={(e) => setD({ ...d, cover: e.target.value })} />
      <div style={{ height: 7 }} />
      <div style={{ display: "flex", gap: 7 }}>
        <input style={{ ...field, flex: 1 }} type="number" min={0}
          placeholder={probing ? "reading…" : "length (auto)"}
          value={d.durationSec || ""}
          onChange={(e) => setD({ ...d, durationSec: e.target.value })} />
        <input style={{ ...field, flex: 1 }} type="number" min={0} placeholder="◆ price (0 = free)"
          value={d.priceDC || ""} onChange={(e) => setD({ ...d, priceDC: e.target.value })} />
      </div>

      <div style={{ height: 12 }} />
      <button onClick={busy ? undefined : submit} style={{
        background: `linear-gradient(150deg, ${C.goldHi}, ${C.gold})`, border: "none",
        color: C.void, fontFamily: C.mono, fontSize: 10, letterSpacing: ".24em",
        padding: "12px 18px", width: "100%",
      }}>{busy ? "PUBLISHING…" : "PUBLISH TRACK"}</button>

      <div style={{ fontFamily: C.sans, fontSize: 13, color: C.smoke, marginTop: 12, lineHeight: 1.5, fontStyle: "italic" }}>
        Upload the audio to the <b>lyraea-assets</b> project first, then publish
        the filename here — length is read off the file itself, you do not have
        to know it. The catalogue stores filenames, never full URLs, which is
        what lets the whole library change hosts by editing one line in CDN.js.
        <br /><br />
        MP3 is the format. Windows Media (.wma) plays in no browser and is
        rejected before it can sit in the library looking healthy.
      </div>

      <ExtrasStudio uid={uid} mine={mine} albums={albums} extras={extras} onFlash={onFlash} />
      <ItemStudio uid={uid} items={items} onFlash={onFlash} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// EXTRAS STUDIO — the things that come WITH a release
// ════════════════════════════════════════════════════════════════════
// The one control that carries the whole distinction is the PARENT
// picker: an extra must name a release, and if you have nothing to
// attach it to, what you have is an ITEM. The form says that in those
// words rather than refusing with a validation error, because "this
// field is required" teaches nobody the difference.
function ExtrasStudio({ uid, mine, albums, extras, onFlash }) {
  const blank = { kind: "lyrics", title: "", parent: "", body: "", src: "" };
  const [d, setD] = useState(blank);
  const [busy, setBusy] = useState(false);

  const mineAlbums = albums.list.filter((al) => al.artistUid === uid);
  const myExtras = extras.filter((e) => e.artistUid === uid);
  const orphans = orphanExtras(myExtras, mine, Object.fromEntries(mineAlbums.map((a) => [a.id, a])));

  const nameOf = (e) => {
    if (e.trackId) return mine.find((t) => t.id === e.trackId)?.title || "— gone —";
    return mineAlbums.find((a) => a.id === e.albumId)?.title || "— gone —";
  };

  const submit = async () => {
    if (!d.parent) return onFlash("PICK WHAT IT BELONGS TO");
    if (!d.title.trim() && !d.body.trim() && !d.src.trim()) return onFlash("NEEDS SOMETHING IN IT");
    const [what, id] = d.parent.split(":");
    setBusy(true);
    try {
      await saveExtra(uid, {
        kind: d.kind,
        title: d.title,
        body: d.body,
        src: d.src,
        trackId: what === "t" ? id : "",
        albumId: what === "a" ? id : "",
      });
      setD({ ...blank, kind: d.kind, parent: d.parent });
      onFlash("ATTACHED");
    } catch (e) { onFlash((e.message || "REFUSED").toUpperCase()); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div style={{ height: 22 }} />
      <Kicker tint={C.gold}>EXTRAS — {myExtras.length}</Kicker>
      <div style={{ height: 10 }} />

      {myExtras.map((e) => (
        <div key={e.id} style={{
          display: "flex", alignItems: "center", gap: 10, padding: "8px 6px",
          borderBottom: `1px solid ${C.edge}`,
        }}>
          <span style={{ fontFamily: C.display, fontSize: 16, color: C.gold, width: 20 }}>
            {KIND_META[e.kind]?.glyph || "◈"}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: C.sans, fontSize: 15, color: C.bone, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {e.title || KIND_META[e.kind]?.label}
            </div>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: C.smoke, letterSpacing: ".12em", textTransform: "uppercase" }}>
              {KIND_META[e.kind]?.label} · ON {nameOf(e)}
            </div>
          </div>
          <button onClick={() => deleteExtra(uid, e.id).then(() => onFlash("DETACHED")).catch((x) => onFlash(x.message))}
            style={ghostBtn}>REMOVE</button>
        </div>
      ))}

      {/* An orphan is an extra whose release is gone. It is invisible to
          listeners already — nothing renders it — so this is a tidy-up
          offer and never an automatic sweep. A cascade that fires on a
          bad read deletes somebody's lyrics for free. */}
      {orphans.length > 0 && (
        <div style={{ marginTop: 10, padding: "10px 12px", border: `1px solid ${C.edge}`, background: C.slab }}>
          <div style={{ fontFamily: C.sans, fontSize: 13, color: C.ash, lineHeight: 1.5 }}>
            {orphans.length} extra{orphans.length > 1 ? "s are" : " is"} attached to
            {orphans.length > 1 ? " releases" : " a release"} that no longer exists.
            Nobody can see {orphans.length > 1 ? "them" : "it"} — they are only taking up space.
          </div>
          <button onClick={() => sweepOrphanExtras(uid, orphans).then((n) => onFlash(`SWEPT ${n}`))}
            style={{ ...ghostBtn, marginTop: 8 }}>SWEEP THEM</button>
        </div>
      )}

      <div style={{ height: 14 }} />
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
        {EXTRA_KINDS.map((k) => (
          <button key={k} onClick={() => setD({ ...d, kind: k })} style={{
            background: "none", border: `1px solid ${d.kind === k ? C.gold : C.edge}`,
            color: d.kind === k ? C.goldHi : C.smoke, fontFamily: C.mono,
            fontSize: 9, letterSpacing: ".16em", padding: "7px 11px",
          }}>{KIND_META[k].glyph} {KIND_META[k].label.toUpperCase()}</button>
        ))}
      </div>

      <div style={{ height: 8 }} />
      <select value={d.parent} onChange={(e) => setD({ ...d, parent: e.target.value })} style={field}>
        <option value="">— what does it belong to? —</option>
        {mine.length > 0 && (
          <optgroup label="Tracks">
            {mine.map((t) => <option key={t.id} value={`t:${t.id}`}>{t.title}</option>)}
          </optgroup>
        )}
        {mineAlbums.length > 0 && (
          <optgroup label="Albums">
            {mineAlbums.map((a) => <option key={a.id} value={`a:${a.id}`}>{a.title}</option>)}
          </optgroup>
        )}
      </select>

      <div style={{ height: 7 }} />
      <input style={field} placeholder={`Title (optional — defaults to "${KIND_META[d.kind].label}")`}
        value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} />
      <div style={{ height: 7 }} />
      {holdsBody(d.kind) ? (
        <textarea
          style={{ ...field, minHeight: 150, resize: "vertical", lineHeight: 1.7 }}
          placeholder={d.kind === "lyrics"
            ? "The words. Leave a BLANK LINE between verses — the gap is kept."
            : "Whatever you want to say about it."}
          value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} />
      ) : (
        <input style={field} placeholder="Image filename on lyraea-assets  (sleeve_back.webp)"
          value={d.src} onChange={(e) => setD({ ...d, src: e.target.value })} />
      )}

      <div style={{ height: 12 }} />
      <button onClick={busy ? undefined : submit} style={{
        background: "none", border: `1px solid ${C.gold}`, color: C.goldHi,
        fontFamily: C.mono, fontSize: 10, letterSpacing: ".24em",
        padding: "12px 18px", width: "100%",
      }}>{busy ? "ATTACHING…" : "ATTACH TO RELEASE"}</button>

      <div style={{ fontFamily: C.sans, fontSize: 13, color: C.smoke, marginTop: 12, lineHeight: 1.5, fontStyle: "italic" }}>
        An extra is <b>part of a release</b> — it arrives with the track or the
        album and it leaves when they do. A painting you made that stands on its
        own is not an extra; publish it below as an <b>item</b> and it gets its
        own place on the shelf.
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// ITEM STUDIO — everything that is not music
// ════════════════════════════════════════════════════════════════════
// Nothing in the schema ever said "song", which was the cheap half of
// being an everything-platform. This is the expensive half: a kind is
// only real once there is somewhere to LOOK at it. Every kind offered
// here has a branch in Extras.js `Viewer` — if you add one to
// ITEM_KINDS without adding the branch, you ship a row that can be
// owned and cannot be opened.
function ItemStudio({ uid, items, onFlash }) {
  const blank = { kind: "art", title: "", artist: "", src: "", body: "", cover: "", priceDC: 0 };
  const [d, setD] = useState(blank);
  const [busy, setBusy] = useState(false);
  const mineItems = items.filter((i) => i.artistUid === uid);

  const submit = async () => {
    if (!d.title.trim()) return onFlash("NEEDS A TITLE");
    if (holdsBody(d.kind) ? !d.body.trim() : !d.src.trim()) {
      return onFlash(holdsBody(d.kind) ? "NEEDS SOME WORDS" : "NEEDS A FILE");
    }
    setBusy(true);
    try {
      await publishItem(uid, { ...d, priceDC: Number(d.priceDC) || 0 });
      setD({ ...blank, kind: d.kind, artist: d.artist });
      onFlash("PUBLISHED");
    } catch (e) { onFlash((e.message || "REFUSED").toUpperCase()); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div style={{ height: 22 }} />
      <Kicker tint={C.gold}>ITEMS — {mineItems.length}</Kicker>
      <div style={{ height: 10 }} />

      {mineItems.map((i) => (
        <div key={i.id} style={{
          display: "flex", alignItems: "center", gap: 10, padding: "8px 6px",
          borderBottom: `1px solid ${C.edge}`,
        }}>
          <span style={{ fontFamily: C.display, fontSize: 16, color: C.gold, width: 20 }}>
            {KIND_META[i.kind]?.glyph || "◈"}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: C.sans, fontSize: 15, color: C.bone, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {i.title}
            </div>
            <div style={{ fontFamily: C.mono, fontSize: 9, color: C.smoke, letterSpacing: ".12em", textTransform: "uppercase" }}>
              {KIND_META[i.kind]?.label} · {i.priceDC ? `◆ ${i.priceDC} — NOT YET SELLABLE` : "FREE"}
            </div>
          </div>
          <button onClick={() => unpublishItem(uid, i.id).then(() => onFlash("REMOVED")).catch((e) => onFlash(e.message))}
            style={ghostBtn}>REMOVE</button>
        </div>
      ))}

      <div style={{ height: 14 }} />
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
        {ITEM_KINDS.map((k) => (
          <button key={k} onClick={() => setD({ ...d, kind: k })} style={{
            background: "none", border: `1px solid ${d.kind === k ? C.gold : C.edge}`,
            color: d.kind === k ? C.goldHi : C.smoke, fontFamily: C.mono,
            fontSize: 9, letterSpacing: ".16em", padding: "7px 11px",
          }}>{KIND_META[k].glyph} {KIND_META[k].label.toUpperCase()}</button>
        ))}
      </div>

      <div style={{ height: 8 }} />
      <input style={field} placeholder="Title" value={d.title}
        onChange={(e) => setD({ ...d, title: e.target.value })} />
      <div style={{ height: 7 }} />
      <input style={field} placeholder="Credit" value={d.artist}
        onChange={(e) => setD({ ...d, artist: e.target.value })} />
      <div style={{ height: 7 }} />
      {holdsBody(d.kind) ? (
        <textarea style={{ ...field, minHeight: 150, resize: "vertical", lineHeight: 1.7 }}
          placeholder="The words." value={d.body}
          onChange={(e) => setD({ ...d, body: e.target.value })} />
      ) : (
        <input style={field} placeholder="Filename on lyraea-assets" value={d.src}
          onChange={(e) => setD({ ...d, src: e.target.value })} />
      )}
      <div style={{ height: 7 }} />
      <div style={{ display: "flex", gap: 7 }}>
        <input style={{ ...field, flex: 1 }} placeholder="Cover filename (optional)"
          value={d.cover} onChange={(e) => setD({ ...d, cover: e.target.value })} />
        <input style={{ ...field, flex: 1 }} type="number" min={0} placeholder="◆ price (0 = free)"
          value={d.priceDC || ""} onChange={(e) => setD({ ...d, priceDC: e.target.value })} />
      </div>

      <div style={{ height: 12 }} />
      <button onClick={busy ? undefined : submit} style={{
        background: "none", border: `1px solid ${C.gold}`, color: C.goldHi,
        fontFamily: C.mono, fontSize: 10, letterSpacing: ".24em",
        padding: "12px 18px", width: "100%",
      }}>{busy ? "PUBLISHING…" : "PUBLISH ITEM"}</button>

      <div style={{ fontFamily: C.sans, fontSize: 13, color: C.smoke, marginTop: 12, lineHeight: 1.5, fontStyle: "italic" }}>
        Anything priced above ◆0 goes up on the shelf and shows its price, and
        nobody can take it — there is no code in this app that spends a Crest.
        Price it at <b>0</b> to let people keep it today.
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE SHELF — everything that is not music
// ════════════════════════════════════════════════════════════════════
// The first surface in the Empire where something changes hands. It is
// deliberately unremarkable: a row, a viewer, and one button that either
// says KEEP or says why it cannot.
function Shelf({ uid, items, owned, onFlash, onOpenArtist }) {
  const [open, setOpen] = useState(null);

  const take = async (it) => {
    const r = await acquire(uid, it.id, "item");
    // A refusal is an ANSWER, not an error. `acquire` resolves rather
    // than throwing for the priced case precisely so this line is not a
    // catch block pretending a business rule is a fault.
    onFlash(r.ok ? "KEPT" : (r.priced ? `◆ ${r.priceDC} — ${PRICED_NOTICE.toUpperCase()}` : r.reason.toUpperCase()));
  };

  if (!items.length) {
    return (
      <div style={{ animation: "lyFade .25s both", padding: "40px 0", textAlign: "center" }}>
        <div style={{ fontFamily: C.display, fontSize: 19, color: C.ash }}>The shelf is bare.</div>
        <div style={{ fontFamily: C.sans, fontSize: 14, color: C.smoke, marginTop: 7 }}>
          Art, writing and books live here. Publish one in the Studio.
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation: "lyFade .25s both" }}>
      <Section idx={1} title="THE SHELF" tint={C.violet}
        right={<span style={{ fontFamily: C.mono, fontSize: 9, color: C.smoke }}>{owned.count} KEPT</span>} />
      {items.map((it) => {
        const isOpen = open === it.id;
        const mine = owned.owns(it.id);
        return (
          <div key={it.id} style={{ borderBottom: `1px solid ${C.edge}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 6px" }}>
              <Cover src={it.cover || it.src} seed={it.id} title={it.title} size={44} />
              <div onClick={() => setOpen(isOpen ? null : it.id)} style={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
                <div style={{
                  fontFamily: C.sans, fontSize: 16, color: C.bone,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>{it.title}</div>
                <div
                  onClick={(e) => { e.stopPropagation(); onOpenArtist?.(it.artistUid); }}
                  style={{
                    fontFamily: C.mono, fontSize: 9, color: C.smoke, letterSpacing: ".12em",
                    textTransform: "uppercase", marginTop: 2,
                  }}>
                  {KIND_META[it.kind]?.label} · {it.artist || "UNKNOWN"}
                </div>
              </div>
              <span style={{
                fontFamily: C.mono, fontSize: 10, letterSpacing: ".1em",
                color: it.priceDC ? C.gold : C.verd,
              }}>{it.priceDC ? `◆ ${it.priceDC}` : "FREE"}</span>
              <button
                onClick={() => (mine ? release(uid, it.id).then(() => onFlash("LET GO")) : take(it))}
                disabled={!uid}
                style={{
                  ...ghostBtn,
                  borderColor: mine ? C.edge : (it.priceDC ? C.edge : C.gold),
                  color: mine ? C.smoke : (it.priceDC ? C.smoke : C.goldHi),
                }}>
                {mine ? "KEPT ✓" : it.priceDC ? "LOCKED" : "KEEP"}
              </button>
            </div>
            {isOpen && (
              <div style={{ padding: "2px 6px 20px" }}>
                <Viewer thing={it} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ALBUM STUDIO — sleeves, made and unmade
// ════════════════════════════════════════════════════════════════════
// Deleting an album RELEASES its tracks rather than destroying them: they
// become singles again. That is the only non-destructive reading of
// "delete the album but keep the music," and it is the behaviour anyone
// would expect the first time they press it by accident.
function AlbumStudio({ uid, artistName, albums, allTracks, onFlash }) {
  const blank = { title: "", artist: artistName || "", cover: "" };
  const [d, setD] = useState(blank);
  const mineAlbums = albums.list.filter((al) => al.artistUid === uid);

  const make = async () => {
    if (!d.title.trim()) return onFlash("NEEDS A TITLE");
    try {
      await saveAlbum(uid, d);
      setD({ ...blank, artist: d.artist });
      onFlash("ALBUM MADE");
    } catch (e) { onFlash(e.message); }
  };

  return (
    <>
      <div style={{ height: 26 }} />
      <Kicker tint={C.gold}>YOUR ALBUMS — {mineAlbums.length}</Kicker>
      <div style={{ height: 10 }} />

      {mineAlbums.length === 0 && (
        <div style={{ fontFamily: C.sans, fontSize: 14, color: C.smoke, paddingBottom: 8 }}>
          No albums yet. Make one below, then put tracks on it with the
          dropdown beside each track above.
        </div>
      )}

      {mineAlbums.map((al) => {
        const n = albumTracks(al.id, allTracks).length;
        return (
          <div key={al.id} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px 6px",
            borderBottom: `1px solid ${C.edge}`,
          }}>
            <Cover src={al.cover} seed={al.id} title={al.title} size={38} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: C.sans, fontSize: 15, color: C.bone }}>{al.title}</div>
              <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: ".12em", color: C.smoke }}>
                {n} TRACK{n === 1 ? "" : "S"}
              </div>
            </div>
            <button
              onClick={() => deleteAlbum(uid, al.id, allTracks)
                .then(() => onFlash("ALBUM DELETED — TRACKS KEPT"))
                .catch((e) => onFlash(e.message))}
              style={ghostBtn}>DELETE</button>
          </div>
        );
      })}

      <div style={{ height: 16 }} />
      <input style={field} placeholder="Album title" value={d.title}
        onChange={(e) => setD({ ...d, title: e.target.value })} />
      <div style={{ height: 7 }} />
      <input style={field} placeholder="Credited to" value={d.artist}
        onChange={(e) => setD({ ...d, artist: e.target.value })} />
      <div style={{ height: 7 }} />
      <input style={field} placeholder="Sleeve image filename or full URL" value={d.cover}
        onChange={(e) => setD({ ...d, cover: e.target.value })} />
      <div style={{ height: 10 }} />
      <button onClick={make} style={{
        background: "none", border: `1px solid ${C.gold}`, color: C.goldHi,
        fontFamily: C.mono, fontSize: 10, letterSpacing: ".24em",
        padding: "11px 18px", width: "100%",
      }}>MAKE ALBUM</button>
      <div style={{ height: 26 }} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// SETS — the shelf of shelves
// ════════════════════════════════════════════════════════════════════
function SetsShelf({ uid, sets, tracks, openSet, onFlash }) {
  const [name, setName] = useState("");

  const make = async () => {
    if (!name.trim()) return onFlash("NEEDS A NAME");
    try { await createSet(uid, name.trim()); setName(""); onFlash("SET MADE"); }
    catch (e) { onFlash(e.message); }
  };

  return (
    <div style={{ animation: "lyFade .25s both" }}>
      <div style={{ display: "flex", gap: 7, marginBottom: 16 }}>
        <input style={{ ...field, flex: 1 }} placeholder="New set" value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && make()} />
        <button onClick={make} style={{
          background: `linear-gradient(150deg, ${C.goldHi}, ${C.gold})`, border: "none",
          color: C.void, fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".2em", padding: "0 16px",
        }}>MAKE</button>
      </div>

      {sets.list.length === 0 && (
        <div style={{ padding: "34px 8px", textAlign: "center" }}>
          <div style={{ fontFamily: C.display, fontSize: 21, color: C.bone }}>No sets yet.</div>
          <div style={{ fontFamily: C.sans, fontSize: 14, color: C.smoke, marginTop: 6 }}>
            Name one above, then hit ＋ on any track.
          </div>
        </div>
      )}

      {sets.list.map((s) => {
        const resolved = resolveSet(s, tracks);
        const cover = resolved[0]?.cover;
        return (
          <div key={s.id} onClick={() => openSet(s.id)} style={{
            display: "flex", alignItems: "center", gap: 11, padding: "9px 6px",
            borderBottom: `1px solid ${C.edge}`, cursor: "pointer",
          }}>
            <Cover src={cover} seed={s.id} title={s.name} size={46} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: C.sans, fontSize: 16, color: C.bone }}>{s.name}</div>
              <div style={{ fontFamily: C.mono, fontSize: 9, letterSpacing: ".14em", color: C.smoke }}>
                {resolved.length} TRACK{resolved.length === 1 ? "" : "S"}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); deleteSet(uid, s.id).then(() => onFlash("DELETED")).catch((err) => onFlash(err.message)); }}
              style={ghostBtn}>DELETE</button>
          </div>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE APP SCREEN
// ════════════════════════════════════════════════════════════════════
export default function Lyraea({ uid, profile, onLogout }) {
  const { tracks, loading } = useCatalogue();
  const lane = useMyLane(uid);
  const sets = useSets(uid);
  const albums = useAlbums();
  const extras = useExtras();
  const items = useItems();
  const owned = useOwned(uid);
  const a = useAudio();

  const [tab, setTab] = useState("library");
  const [view, setView] = useState(null);      // {type:"artist"|"set", id} | null
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState(false);
  const [adding, setAdding] = useState(null);  // track pending a set
  const [flash, setFlash] = useState("");

  // Wire play counting into the engine, restore the last session, and
  // take the keyboard. All one-time — the engine outlives this component.
  useEffect(() => {
    setPlayReporter((trackId) => countPlay(uid, trackId));
    restoreSession();
    return attachKeyboard();
  }, [uid]);

  const say = (msg) => { setFlash(msg); setTimeout(() => setFlash(""), 2200); };

  // ── AUDIOBOOKS — drop a Readers MP3 anywhere, or pick one. ──────────
  // Plays from the listener's own device (book.js says why not the CDN).
  const bookInput = React.useRef(null);
  const [dropping, setDropping] = useState(false);
  const openBookFiles = async (files) => {
    try {
      const t = await openBook(files);
      const old = getAudioState().track;
      loadQueue([t], 0, true);
      // The previous dropped book's blob holds its whole file in memory.
      if (old?.local && old.src !== t.src) URL.revokeObjectURL(old.src);
      setSheet(true);
      say(t.book.timed ? `${t.book.chapters.length} CHAPTERS · WORDS SYNCED`
                       : `${t.book.chapters.length} CHAPTERS`);
    } catch (e) { say(e.message.toUpperCase()); }
  };
  useEffect(() => {
    const over = (e) => {
      if (![...(e.dataTransfer?.types || [])].includes("Files")) return;
      e.preventDefault(); setDropping(true);
    };
    const leave = (e) => { if (!e.relatedTarget) setDropping(false); };
    const drop = (e) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault(); setDropping(false); openBookFiles(e.dataTransfer.files);
    };
    window.addEventListener("dragover", over);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", drop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Learn a missing duration from playback and write it back — once, only
  // for your own tracks, only when the stored value is empty. Tracks
  // published before the probe existed heal themselves the first time
  // their artist listens to them.
  useEffect(() => {
    const t = a.track;
    if (!t || t.artistUid !== uid || t.durationSec || !a.duration) return;
    setTrackDuration(uid, t.id, a.duration).catch(() => {});
  }, [a.track, a.duration, uid]);

  const mine = useMemo(() => tracks.filter((t) => t.artistUid === uid), [tracks, uid]);

  // What this listener actually keeps coming back to. Derived from their
  // own play counts, so it needs no extra stored list and cannot drift.
  const onRepeat = useMemo(() => {
    const byId = new Map(tracks.map((t) => [t.id, t]));
    return Object.entries(lane.plays || {})
      .sort((x, y) => y[1] - x[1])
      .slice(0, 6)
      .map(([id]) => byId.get(id))
      .filter(Boolean);
  }, [lane.plays, tracks]);

  const shelf = useMemo(() => {
    let list = tracks;
    if (tab === "liked") list = list.filter((t) => lane.isLiked(t.id));
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((t) =>
        (t.title || "").toLowerCase().includes(needle) ||
        (t.artist || "").toLowerCase().includes(needle));
    }
    return list;
  }, [tracks, tab, q, lane]);

  // A priced track is not playable yet, and the queue handed to the engine
  // is filtered the same way — so "next" can never walk into something
  // that refuses to play.
  const playableOf = (list) => list.filter((t) => !t.priceDC);

  const start = (t, list) => {
    playTrack(t, playableOf(list?.length ? list : shelf));
    setSheet(false);
  };

  const openArtist = (artistUid) => { if (artistUid) { setView({ type: "artist", id: artistUid }); window.scrollTo(0, 0); } };
  const openAlbum  = (albumId)   => { if (albumId)   { setView({ type: "album",  id: albumId   }); window.scrollTo(0, 0); } };

  // NOTE: `key` is deliberately NOT in here. React 19 warns when a key
  // arrives via a spread props object and ignores it — every row would
  // silently lose its identity and re-render wrong on reorder. Keys are
  // passed explicitly at each call site.
  const rowProps = (t, i, list, extra = {}) => ({
    track: t,
    index: i,
    current: a.track?.id === t.id,
    playing: a.playing,
    liked: lane.isLiked(t.id),
    locked: !!t.priceDC,
    liveDuration: a.duration,
    onPlay: () => start(t, list),
    onLike: () => toggleLike(uid, t.id, !lane.isLiked(t.id)).catch((e) => say(e.message)),
    onArtist: openArtist,
    onAdd: () => setAdding(t),
    onQueue: () => say(`QUEUED · ${addToQueue(t)}`),
    hasExtras: extras.some((e) => e.trackId === t.id),
    ...extra,
  });

  const openSet = view?.type === "set" ? sets.byId(view.id) : null;
  const setTracks = useMemo(
    () => (openSet ? resolveSet(openSet, tracks) : []),
    [openSet, tracks]
  );

  return (
    <div style={{ minHeight: "100%", background: C.void, display: "flex", flexDirection: "column" }}>
      {/* ── MASTHEAD ─────────────────────────────────────────── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 300,
        background: `linear-gradient(180deg, ${C.void}, ${C.void}e8)`,
        backdropFilter: "blur(18px) saturate(150%)", borderBottom: `1px solid ${C.edge}`,
        padding: `calc(12px + env(safe-area-inset-top,0px)) 16px 10px`,
      }}>
        {/* the rail's accent hairline — straight off the site */}
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: -1, height: 1,
          background: `linear-gradient(90deg, transparent, ${C.gold} 50%, transparent)`,
          opacity: .45, pointerEvents: "none",
        }} />
        <div style={{ ...WRAP, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10 }}>
          <div onClick={() => { setView(null); setTab("library"); }} style={{ cursor: "pointer" }}>
            <Kicker size={8.5}>THE ILLEGAL EMPIRE</Kicker>
            <div style={{
              fontFamily: C.display, fontWeight: 800, fontSize: 30, lineHeight: 1,
              letterSpacing: ".02em",
              background: `linear-gradient(105deg, #6b5220 0%, ${C.gold} 20%, ${C.goldHi} 32%, #fffbe8 40%, ${C.goldHi} 52%, ${C.gold} 70%, #8a6a26 100%)`,
              WebkitBackgroundClip: "text", backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>LYRAEA</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <a className="ly-chip" href={`${PIER_URL}?uid=${encodeURIComponent(uid)}`}
              style={{ textDecoration: "none" }}>← PIER</a>
            {onLogout && <button className="ly-chip" onClick={onLogout}>OUT</button>}
          </div>
        </div>

        <div style={{ ...WRAP, display: "flex", gap: 15, marginTop: 12 }}>
          {[["library", "LIBRARY"], ["liked", "LIKED"], ["sets", "SETS"], ["shelf", "SHELF"], ["studio", "STUDIO"]].map(([k, l]) => (
            <button key={k} className={`ly-tab${!view && tab === k ? " on" : ""}`}
              onClick={() => { setTab(k); setView(null); }}>{l}</button>
          ))}
        </div>
      </div>

      {/* ── BODY ─────────────────────────────────────────────── */}
      <div className="ly-scroll" style={{
        flex: 1, overflowY: "auto", padding: "12px 16px",
        paddingBottom: `calc(var(--bar-h) + var(--safe-b) + 26px)`,
      }}>
       <div style={WRAP}>
        {/* An engine error belongs on the screen the listener is looking
            at, not only inside the now-playing sheet. */}
        {a.error && (
          <div style={{
            marginBottom: 12, padding: "10px 12px",
            border: `1px solid ${C.gold}66`, background: "#d4a03c14",
            fontFamily: C.sans, fontSize: 13.5, color: C.bone, lineHeight: 1.45,
          }}>{a.error}</div>
        )}

        {/* ── ARTIST ─────────────────────────────────────────── */}
        {view?.type === "artist" && (
          <ArtistPage
            artistUid={view.id}
            tracks={tracks}
            albums={albums.list}
            onOpenAlbum={openAlbum}
            current={a.track?.id}
            playing={a.playing}
            onBack={() => setView(null)}
            onPlay={(t, list) => start(t, list)}
            onPlayAll={(list) => { if (list.length) { loadQueue(list, 0, true); setSheet(false); } }}
          />
        )}

        {/* ── ONE ALBUM ──────────────────────────────────────── */}
        {view?.type === "album" && (
          <AlbumPage
            album={albums.byId(view.id)}
            tracks={tracks}
            current={a.track?.id}
            playing={a.playing}
            liked={(id) => lane.isLiked(id)}
            onBack={() => setView(null)}
            onArtist={openArtist}
            onPlay={(t, list) => start(t, list)}
            onPlayAll={(list) => { if (list.length) { loadQueue(list, 0, true); setSheet(false); } }}
            onLike={(t) => toggleLike(uid, t.id, !lane.isLiked(t.id)).catch((e) => say(e.message))}
          />
        )}

        {/* ── ONE SET ────────────────────────────────────────── */}
        {view?.type === "set" && (
          <div style={{ animation: "lyFade .25s both" }}>
            <button onClick={() => setView(null)} style={{
              background: "none", border: "none", color: C.ash, padding: "2px 0 12px",
              fontFamily: C.mono, fontSize: 10, letterSpacing: ".26em",
            }}>← SETS</button>

            <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 16 }}>
              <Cover src={setTracks[0]?.cover} seed={openSet?.id} title={openSet?.name} size={78} glow />
              <div style={{ minWidth: 0 }}>
                <Kicker tint={C.gold}>SET</Kicker>
                <div style={{ fontFamily: C.display, fontWeight: 800, fontSize: 25, color: C.bone, lineHeight: 1.1 }}>
                  {openSet?.name || "Gone"}
                </div>
                <button
                  onClick={() => { const p = playableOf(setTracks); if (p.length) { loadQueue(p, 0, true); } }}
                  disabled={!playableOf(setTracks).length}
                  style={{
                    marginTop: 9, border: "none", color: C.void,
                    background: `linear-gradient(150deg, ${C.goldHi}, ${C.gold})`,
                    fontFamily: C.mono, fontSize: 9.5, letterSpacing: ".2em", padding: "9px 15px",
                    opacity: playableOf(setTracks).length ? 1 : .4,
                  }}>▶ PLAY SET</button>
              </div>
            </div>

            {setTracks.length === 0 && (
              <div style={{ fontFamily: C.sans, fontSize: 14, color: C.smoke, padding: "18px 2px" }}>
                Empty. Hit ＋ on any track to put it here.
              </div>
            )}

            {setTracks.map((t, i) => (
              <TrackRow key={t.id} {...rowProps(t, i, setTracks, {
                onAdd: null,
                onRemove: () => removeFromSet(uid, openSet.id, t.id).then(() => say("REMOVED")).catch((e) => say(e.message)),
              })} />
            ))}
          </div>
        )}

        {/* ── SHELVES ────────────────────────────────────────── */}
        {!view && tab === "studio" && (
          <Studio uid={uid} artistName={profile?.displayName || profile?.name}
            mine={mine} albums={albums} allTracks={tracks}
            extras={extras} items={items} onFlash={say} />
        )}

        {!view && tab === "shelf" && (
          <Shelf uid={uid} items={items} owned={owned} onFlash={say}
            onOpenArtist={(id) => id && setView({ type: "artist", id })} />
        )}

        {!view && tab === "sets" && (
          <SetsShelf uid={uid} sets={sets} tracks={tracks}
            openSet={(id) => setView({ type: "set", id })} onFlash={say} />
        )}

        {!view && (tab === "library" || tab === "liked") && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              <div style={{ position: "relative", flex: 1, minWidth: 190 }}>
                <input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search the library"
                  style={{ ...field, paddingRight: 34 }}
                />
                {q && (
                  <button onClick={() => setQ("")} title="Clear" style={{
                    position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", color: C.smoke,
                    fontFamily: C.mono, fontSize: 12, padding: 6,
                  }}>✕</button>
                )}
              </div>

              <button className="ly-chip solid"
                disabled={!playableOf(shelf).length}
                onClick={() => { const p = playableOf(shelf); if (p.length) loadQueue(p, 0, true); }}
              >▶ PLAY ALL</button>

              <button className="ly-chip"
                disabled={playableOf(shelf).length < 2}
                onClick={() => {
                  const p = playableOf(shelf);
                  if (p.length < 2) return;
                  // Shuffle-all starts somewhere random AND turns shuffle on,
                  // which is what people mean by the button. Starting at track
                  // one with shuffle enabled feels like it ignored the press.
                  if (!a.shuffle) toggleShuffle();
                  loadQueue(p, Math.floor(Math.random() * p.length), true);
                }}
              >⤨ SHUFFLE</button>

              <button className="ly-chip" title="Play an audiobook made in Readers — chapters and synced words included"
                onClick={() => bookInput.current?.click()}
              >📖 OPEN AUDIOBOOK</button>
              <input ref={bookInput} type="file" multiple hidden
                accept="audio/*,.mp3,.m4b,.json"
                onChange={(e) => { if (e.target.files?.length) openBookFiles(e.target.files); e.target.value = ""; }} />
            </div>

            {tab === "library" && !q && albums.list.length > 0 && (
              <>
                <Section idx={1} title="ALBUMS" tint={C.gold} />
                <AlbumRail albums={albums.list} tracks={tracks} onOpen={openAlbum} bare />
              </>
            )}

            {tab === "library" && !q && onRepeat.length > 1 && (
              <div style={{ marginBottom: 18 }}>
                <Section idx={albums.list.length ? 2 : 1} title="ON REPEAT" tint={C.verd} />
                <div className="ly-scroll" style={{
                  display: "flex", gap: 10, overflowX: "auto", padding: "10px 0 4px",
                }}>
                  {onRepeat.map((t) => (
                    <div key={t.id} className="ly-sleeve" onClick={() => start(t, onRepeat)}
                      style={{ width: 96, flex: "0 0 auto" }}>
                      <Cover src={t.cover} seed={t.id} title={t.title} size={96} />
                      <div className="ly-sleeve-title" style={{
                        fontFamily: C.sans, fontSize: 13, color: C.bone, marginTop: 5,
                        transition: "color .3s",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{t.title}</div>
                      <div style={{
                        fontFamily: C.mono, fontSize: 8.5, color: C.smoke, letterSpacing: ".12em",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      }}>{(t.artist || "").toUpperCase()}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loading && (
              <div style={{
                fontFamily: C.mono, fontSize: 10, letterSpacing: ".3em", color: C.smoke,
                padding: 30, textAlign: "center", animation: "lyPulse 1.4s infinite",
              }}>OPENING THE LIBRARY</div>
            )}

            {!loading && shelf.length === 0 && (
              <div style={{ padding: "44px 8px", textAlign: "center" }}>
                <div style={{ fontFamily: C.display, fontSize: 22, color: C.bone }}>
                  {q ? "Nothing matches." : tab === "liked" ? "Nothing kept yet." : "The library is empty."}
                </div>
                <div style={{ fontFamily: C.sans, fontSize: 14, color: C.smoke, marginTop: 7 }}>
                  {q ? "Try a shorter search."
                    : tab === "liked" ? "Tap the ◇ beside a track to keep it."
                    : "Publish the first track in the Studio."}
                </div>
              </div>
            )}

            {shelf.length > 0 && (
              <Section
                idx={1 + (tab === "library" && !q && albums.list.length ? 1 : 0)
                       + (tab === "library" && !q && onRepeat.length > 1 ? 1 : 0)}
                title={tab === "liked" ? "KEPT" : "EVERYTHING"}
                right={<span style={{ fontFamily: C.mono, fontSize: 9, color: C.smoke, letterSpacing: ".16em" }}>
                  {shelf.length}
                </span>} />
            )}

            {shelf.map((t, i) => <TrackRow key={t.id} {...rowProps(t, i, shelf)} />)}

            {shelf.some((t) => t.priceDC) && (
              <div style={{
                marginTop: 16, padding: "10px 12px", border: `1px solid ${C.edge}`,
                fontFamily: C.sans, fontSize: 13, color: C.smoke, lineHeight: 1.5,
              }}>
                Free tracks play, and free things on the shelf can be kept. A
                priced track still shows its price and will not sell — buying
                waits on the currency becoming server-authoritative, because an
                economy built on a balance nobody can verify is fiction.
              </div>
            )}
          </>
        )}
       </div>
      </div>

      {flash && (
        <div style={{
          position: "fixed", left: "50%", transform: "translateX(-50%)",
          bottom: `calc(var(--bar-h) + var(--safe-b) + 18px)`, zIndex: 700,
          background: C.obsidian, border: `1px solid ${C.gold}66`,
          padding: "8px 15px", fontFamily: C.mono, fontSize: 9.5,
          letterSpacing: ".2em", color: C.goldHi, maxWidth: "86vw", textAlign: "center",
        }}>{flash}</div>
      )}

      {adding && (
        <AddToSet uid={uid} track={adding} sets={sets}
          onClose={() => setAdding(null)} onFlash={say} />
      )}

      {dropping && (
        <div style={{
          position: "fixed", inset: 12, zIndex: 1000, pointerEvents: "none",
          border: `2px dashed ${C.gold}`, background: "#0a0a12cc",
          display: "grid", placeItems: "center",
          fontFamily: C.mono, fontSize: 12, letterSpacing: ".3em", color: C.goldHi,
        }}>DROP THE AUDIOBOOK</div>
      )}

      <NowBar onOpen={() => setSheet(true)} onOpenArtist={openArtist} />
      {sheet && <NowSheet onClose={() => setSheet(false)} onOpenArtist={(u) => { setSheet(false); openArtist(u); }} />}
    </div>
  );
}
